import { useState, useCallback } from "react";
import type { MentionItem, MentionContext } from "@/types/mention";
import type { RAGContext } from "@/hooks/useRAG";

interface UseCompactMentionsOptions {
  value: string;
  setValue: (value: string) => void;
  mode: "search" | "chat";
  context: RAGContext;
  setContext: React.Dispatch<React.SetStateAction<RAGContext>>;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

interface UseCompactMentionsResult {
  mentionContext: MentionContext & { triggeredByButton?: boolean };
  setMentionContext: React.Dispatch<
    React.SetStateAction<MentionContext & { triggeredByButton?: boolean }>
  >;
  handleTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleMentionSelect: (item: MentionItem) => void;
  handleAtButtonClick: () => void;
}

/**
 * Hook to manage mention functionality in CompactUnifiedSearch
 * Handles @ detection, mention menu state, and mention insertion
 */
export function useCompactMentions({
  value,
  setValue,
  mode,
  context,
  setContext,
  textareaRef,
}: UseCompactMentionsOptions): UseCompactMentionsResult {
  // Mention state
  const [mentionContext, setMentionContext] = useState<
    MentionContext & { triggeredByButton?: boolean }
  >({
    showMenu: false,
    searchQuery: "",
    cursorPosition: 0,
    mentionStartIndex: -1,
  });

  // Handle text changes with @ detection
  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const cursorPos = e.target.selectionStart || 0;

      setValue(newValue);

      // @ mention detection in chat mode
      if (mode === "chat") {
        const textBeforeCursor = newValue.substring(0, cursorPos);
        const lastAtIndex = textBeforeCursor.lastIndexOf("@");

        if (lastAtIndex !== -1) {
          const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
          if (!textAfterAt.includes(" ")) {
            setMentionContext({
              showMenu: true,
              searchQuery: textAfterAt,
              cursorPosition: cursorPos,
              mentionStartIndex: lastAtIndex,
            });
            return;
          }
        }
      }

      // Hide mention menu if not needed
      if (mentionContext.showMenu) {
        setMentionContext((prev) => ({ ...prev, showMenu: false }));
      }
    },
    [mode, mentionContext.showMenu, setValue]
  );

  // Handle mention selection
  const handleMentionSelect = useCallback(
    (item: MentionItem) => {
      const { mentionStartIndex, cursorPosition } = mentionContext;
      const hasAtSymbol = value.charAt(mentionStartIndex) === "@";
      const beforeMention = value.substring(0, mentionStartIndex);
      const afterCursor = value.substring(
        hasAtSymbol ? cursorPosition : mentionStartIndex
      );
      const mentionText = `@${item.label} `;
      const newValue = beforeMention + mentionText + afterCursor;

      setValue(newValue);

      // Update context based on mention type
      if (item.type === "project") {
        const currentProjects = context.projectIds || [];
        if (!currentProjects.includes(item.value)) {
          setContext((prev) => ({
            ...prev,
            projectIds: [...currentProjects, item.value],
          }));
        }
      } else if (item.type === "branch") {
        const currentBranches = context.branchNames || [];
        if (!currentBranches.includes(item.value)) {
          setContext((prev) => ({
            ...prev,
            branchNames: [...currentBranches, item.value],
          }));
        }
      } else if (item.type === "time") {
        setContext((prev) => ({
          ...prev,
          timeFilter: item.value as RAGContext["timeFilter"],
        }));
      } else if (item.type === "agent") {
        const currentAgents = context.agentNames || [];
        if (!currentAgents.includes(item.value)) {
          setContext((prev) => ({
            ...prev,
            agentNames: [...currentAgents, item.value],
          }));
        }
      }

      // Close menu and restore focus
      setMentionContext({
        showMenu: false,
        searchQuery: "",
        cursorPosition: 0,
        mentionStartIndex: -1,
      });

      // Set cursor after mention
      const newCursorPos = beforeMention.length + mentionText.length;
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
        textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
      });
    },
    [value, mentionContext, context, setValue, setContext, textareaRef]
  );

  // Handle @ button click
  const handleAtButtonClick = useCallback(() => {
    if (textareaRef.current) {
      const cursorPos = textareaRef.current.selectionStart || 0;
      setMentionContext({
        showMenu: true,
        searchQuery: "",
        cursorPosition: cursorPos,
        mentionStartIndex: cursorPos,
        triggeredByButton: true,
      });
      textareaRef.current.focus();
    }
  }, [textareaRef]);

  return {
    mentionContext,
    setMentionContext,
    handleTextChange,
    handleMentionSelect,
    handleAtButtonClick,
  };
}
